// Text for the download page in English (/download) and its translations
// (/fr, /es, /it, /de and /nl /download), plus the pricing cards it shows.
// Prices, versions and installer links are not translated. The translations are
// awaiting native-speaker review (see AWAITING_REVIEW).
//
// Strings with markup are rendered with v-html; W() marks the white highlights
// the English page already used.

const W = (s) => `<span class="text-white">${s}</span>`;
const R = (s) => `<span class="text-[#FF0000]">${s}</span>`;

export const DOWNLOAD_LANGS = ['fr', 'es', 'it', 'de', 'nl'];
export const AWAITING_REVIEW = ['fr', 'es', 'it', 'de', 'nl'];

// The desktop app's own menu labels in each language (extraResources/locales in the
// app repo), so instructions match what users see. Italian "Crea Account" was
// "Crea Acconto" in the app before Rally-Navigator-2---Desktop#761.
export const APP_UI = {
  en: { subscription: 'My Subscription', register: 'Login / Register' },
  fr: { subscription: 'Mon abonnement', register: 'Créer un compte' },
  es: { subscription: 'Mi Suscripción', register: 'Crear Cuenta' },
  it: { subscription: 'Il mio abbonamento', register: 'Crea Account' },
  de: { subscription: 'Mein Abo', register: 'Konto erstellen' },
  // The app has no Dutch translation; Dutch users see the English menus.
  nl: { subscription: 'My Subscription', register: 'Login / Register' },
};

/** /download for English, /{lang}/download for the translated languages. */
export function downloadPath(lang) {
  return DOWNLOAD_LANGS.includes(lang) ? `/${lang}/download` : '/download';
}

export const DOWNLOAD_CONTENT = {
  en: {
    title: 'Download Rally Navigator - Windows, Mac & Linux Rally Software',
    description:
      'Download Rally Navigator for Windows, macOS, and Linux. Free trial available. Create professional FIA-compliant rally roadbooks on your desktop.',
    h1: 'Download Rally Navigator',
    btnWindows: 'Download For Windows 10+ 64-bit',
    btnMac: 'Download For MacOS',
    btnLinux: 'Download For Linux',
    latestVersion: 'Latest version:',
    released: 'Released',
    whatsNew: "See what's new",
    trust: ['Code-signed &amp; Apple-notarized', 'Published by Rally Navigator, LLC', 'Secure HTTPS download'],
    previousTitle: 'Problems with current release? Download previous version here',
    versionWord: 'Version',
    howH2: 'How to Download, Install and Register',
    newH3: 'New to Rally Navigator?',
    newSteps: [
      '1) Download and install Rally Navigator',
      `2) Open the program, click ${W('My Subscription')} in the top menu, then ${W('Login / Register')} to create an account`,
      "3) Click the link in the activation email we send you (check your spam folder if it hasn't arrived in a few minutes)",
      "4) Log in &mdash; you're on your 7 Day Free Trial. Build your first roadbook!",
      '5) Upgrade to Premium or Pro to keep your advanced features',
    ],
    existingH3: 'Already Have an Account?',
    existing: [
      `${W('New computer:')} install Rally Navigator and log in with the same email &mdash; your subscription applies automatically on as many of your own computers as you like.`,
      `${W('Already installed:')} no need to download again. Rally Navigator checks for updates each time it starts and offers to install the new version for you.`,
    ],
    plansH2: 'Plans &amp; Pricing',
    plansP: `Start with a 7 Day Free Trial, then choose your plan inside the app under ${W('My Subscription')}.`,
    trialH2: '7 Day Free Trial',
    trialPs: [
      `Rally Navigator ${W('7 Day Free Trial')} includes ${W('all Premium and Pro features')} &mdash; Advanced Map Tools, FIA/Dakar Navigation Icons, and full PDF &amp; GPX export. It's the complete app, free, for a week.`,
      `Free user Roadbooks are limited to ${W('25&nbsp;km or 25 waypoints')}. Build a Roadbook, learn the workflow, and download your Roadbook details as PDF and OpenRally GPX.`,
      `Ready for full-length events? ${R('Upgrade to Premium or Pro')} inside the app under ${W('My Subscription')} for unlimited Roadbooks with no distance or waypoint limit.`,
      'After the 7 Day Free Trial expires, users can still make Roadbooks, but can no longer download PDF or GPX files.',
    ],
    newFeaturesH2: 'NEW FEATURES',
    newFeatures: [
      'MapBox 3D Terrain Imagery',
      'Runs - Saves - Prints PDF on Local Computer',
      'Share via Dropbox, iCloud, OneDrive',
      'Adjustable Waypoint &amp; Validation Radius Details Show on Map',
      'On Screen Roadbook Auto Highlighting',
      'FIA/Dakar Navigation Icons in SVG',
    ],
    feedbackH2: 'PLEASE SHARE YOUR FEEDBACK',
    howItWorksH2: 'RALLY NAVIGATOR &#8211; HOW IT WORKS',
    ccFormat: 'Cross Country Format',
    rrFormat: 'Road Rally Format',
    featuresA: ['Beautiful MapBox and Google 3D Map Imagery', 'Load, Save and Print PDF Roadbooks - No Network Connection Required'],
    featuresB: ['Improved On Screen Roadbook, Route &amp; Map Highlighting', 'Google KMZ/KML File Support'],
    featuresC: ['Faster and Better Drawing Tools - Improved Toolbar Layout Options', 'Support for Digital Roadbooks + OpenRally GPX Format'],
    ccrrH2: 'CROSS COUNTRY AND ROAD &amp; STAGE RALLY ROADBOOKS',
    alt: {
      toolbar: 'Rally Navigator desktop roadbook editor toolbar',
      ccFormat: 'Cross-country rally roadbook format in Rally Navigator',
      rrFormat: 'Road and stage rally roadbook format in Rally Navigator',
      pdf: 'Rally Navigator printing a roadbook PDF with MapBox maps',
      crossCountry: 'Cross-country rally roadbook created with Rally Navigator',
      ccImage: 'CC Roadbook Image (1)',
      rrImage: 'Road Rally Roadbook (2)',
    },
    downloadLinkText: 'Download Rally Navigator',
    phone: {
      message: 'Rally Navigator is a desktop app for Windows, Mac and Linux. Open this page on your computer to download.',
      copy: 'Copy Link',
      copied: 'Link Copied ✓',
    },
    pricing: {
      trialAlt: 'Free Trial',
      trialH3: '7 Day Free Trial',
      trialDuration: '7 Days',
      trialNote: 'Log In or Sign Up to Start Your Free Trial',
      trialPerks: ['Roadbooks Limited to 25km and 25 Waypoints', 'Active 7 Day Free Trial'],
      trialPerksMore: ['ALL Advanced Premium and Pro Features', 'Download Roadbook PDF and GPX'],
      premiumDuration: 'Per Year',
      premiumNote: 'Roadbooks for Personal Use',
      premiumPerkHead: 'Download Roadbook PDF and GPX',
      premiumPerks: [
        'Export to All Digital Roadbook Devices',
        'Export to Rally Tracking Devices',
        'Direct to PDF or Digital Roadbooks',
        'Supports A5, A4, Legal, Letter, and Roll',
        'Follow Roads Routing Function',
        'Import Tracks to Create a Roadbook',
        'Intelligent Compass Heading Features',
        'Not for Commercial Use',
      ],
      proDuration: 'Per Year',
      proNote: 'Roadbooks for Commercial Use',
      proPlus: 'Everything from Premium - PLUS:',
      pro: {
        'pro-license': 'Pro License for Commercial Rally Events',
        'commercial-use': "Removes 'Not for Commercial Use'",
        'header-image': 'Custom Roadbook Header Image',
        'map-sources': 'Additional Map Sources',
        'custom-icons': 'Import Custom Navigation Icons',
        'qr-code': 'Custom QR Code in Header &amp; Waypoints',
        'speed-elevation': 'Show Speed &amp; Elevation on Map',
        'gps-recorder': 'GPS Roadbook Recorder LIVE',
        'gps-validator': 'GPS Roadbook Validator',
        'elevation-profile': 'Elevation Profile &amp; Data',
        'track-color': 'Show Track Color On Map',
        'moto-rolls': 'Multi-wide Moto Rolls',
        'waypoint-numbers': 'Waypoint Numbers on Map',
        'waypoint-controls': 'Waypoint Control Icons on Map',
        'street-view': 'Street View Image to Roadbook',
        'track-distance-hover': 'Hover Over Track Distance Tool',
        'et-distance': 'Add Track Distance/CAP',
        'tulip-assist': 'Tulip Assist',
        'zone-controls': 'Regroup, Service, Zone Control',
        'smart-icons': 'Drag and Drop Smart Icons',
        'kmz-gpx-export': 'KMZ and GPX Export with Route Details',
      },
    },
  },

  fr: {
    title: 'Télécharger Rally Navigator – Logiciel de roadbook pour Windows, Mac et Linux',
    description:
      'Téléchargez Rally Navigator pour Windows, macOS et Linux. Essai gratuit de 7 jours. Créez des roadbooks de rallye professionnels conformes FIA sur votre ordinateur.',
    h1: 'Télécharger Rally Navigator',
    btnWindows: 'Télécharger pour Windows 10+ 64 bits',
    btnMac: 'Télécharger pour macOS',
    btnLinux: 'Télécharger pour Linux',
    latestVersion: 'Dernière version :',
    released: 'Publiée le',
    whatsNew: 'Voir les nouveautés',
    trust: ['Signé et notarisé par Apple', 'Édité par Rally Navigator, LLC', 'Téléchargement HTTPS sécurisé'],
    previousTitle: 'Un problème avec la version actuelle ? Téléchargez la version précédente ici',
    versionWord: 'Version',
    howH2: 'Télécharger, installer et créer votre compte',
    newH3: 'Nouveau sur Rally Navigator ?',
    newSteps: [
      '1) Téléchargez et installez Rally Navigator',
      `2) Ouvrez le logiciel, cliquez sur ${W('Mon abonnement')} dans le menu du haut, puis sur ${W('Créer un compte')} (ou ${W('Connexion')} si vous en avez déjà un)`,
      '3) Cliquez sur le lien de l’e-mail d’activation que nous vous envoyons (vérifiez vos spams s’il n’arrive pas d’ici quelques minutes)',
      '4) Connectez-vous &mdash; votre essai gratuit de 7 jours commence. Créez votre premier roadbook !',
      '5) Passez à Premium ou Pro pour conserver les fonctionnalités avancées',
    ],
    existingH3: 'Vous avez déjà un compte ?',
    existing: [
      `${W('Nouvel ordinateur :')} installez Rally Navigator et connectez-vous avec la même adresse e-mail &mdash; votre abonnement s’applique automatiquement, sur autant de vos ordinateurs que vous le souhaitez.`,
      `${W('Déjà installé :')} inutile de le télécharger à nouveau. Rally Navigator recherche les mises à jour à chaque démarrage et vous propose d’installer la nouvelle version.`,
    ],
    plansH2: 'Formules et tarifs',
    plansP: `Commencez par l’essai gratuit de 7 jours, puis choisissez votre formule dans l’application, sous ${W('Mon abonnement')}.`,
    trialH2: 'Essai gratuit de 7 jours',
    trialPs: [
      `L’${W('essai gratuit de 7 jours')} de Rally Navigator inclut ${W('toutes les fonctionnalités Premium et Pro')} &mdash; outils cartographiques avancés, icônes de navigation FIA/Dakar et export complet PDF et GPX. C’est l’application complète, gratuite pendant une semaine.`,
      `Les roadbooks des utilisateurs gratuits sont limités à ${W('25&nbsp;km ou 25 waypoints')}. Créez un roadbook, apprenez le fonctionnement et téléchargez-le en PDF et en OpenRally GPX.`,
      `Prêt pour des épreuves complètes ? ${R('Passez à Premium ou Pro')} dans l’application, sous ${W('Mon abonnement')}, pour des roadbooks illimités, sans limite de distance ni de waypoints.`,
      'Après la fin de l’essai gratuit de 7 jours, vous pouvez toujours créer des roadbooks, mais vous ne pouvez plus télécharger les fichiers PDF ou GPX.',
    ],
    newFeaturesH2: 'NOUVEAUTÉS',
    newFeatures: [
      'Cartographie 3D MapBox du relief',
      'Fonctionne, enregistre et imprime en PDF sur votre ordinateur',
      'Partage via Dropbox, iCloud, OneDrive',
      'Waypoints et rayons de validation réglables, affichés sur la carte',
      'Surlignage automatique du roadbook à l’écran',
      'Icônes de navigation FIA/Dakar en SVG',
    ],
    feedbackH2: 'DONNEZ-NOUS VOTRE AVIS',
    howItWorksH2: 'RALLY NAVIGATOR &#8211; COMMENT ÇA MARCHE',
    ccFormat: 'Format cross-country',
    rrFormat: 'Format rallye routier',
    featuresA: ['Superbes images cartographiques 3D MapBox et Google', 'Ouvrez, enregistrez et imprimez vos roadbooks en PDF - sans connexion réseau'],
    featuresB: ['Surlignage amélioré du roadbook, de l’itinéraire et de la carte à l’écran', 'Prise en charge des fichiers Google KMZ/KML'],
    featuresC: ['Outils de dessin plus rapides et plus précis - barre d’outils améliorée', 'Prise en charge des roadbooks numériques + format OpenRally GPX'],
    ccrrH2: 'ROADBOOKS CROSS-COUNTRY ET RALLYE ROUTIER &amp; SPÉCIALES',
    alt: {
      toolbar: 'Barre d’outils de l’éditeur de roadbook Rally Navigator',
      ccFormat: 'Format de roadbook cross-country dans Rally Navigator',
      rrFormat: 'Format de roadbook de rallye routier dans Rally Navigator',
      pdf: 'Rally Navigator imprimant un roadbook PDF avec les cartes MapBox',
      crossCountry: 'Roadbook de rallye cross-country créé avec Rally Navigator',
      ccImage: 'Exemple de roadbook cross-country',
      rrImage: 'Exemple de roadbook de rallye routier',
    },
    downloadLinkText: 'Télécharger Rally Navigator',
    phone: {
      message: 'Rally Navigator est une application pour ordinateur (Windows, Mac et Linux). Ouvrez cette page sur votre ordinateur pour la télécharger.',
      copy: 'Copier le lien',
      copied: 'Lien copié ✓',
    },
    pricing: {
      trialAlt: 'Essai gratuit',
      trialH3: 'Essai gratuit de 7 jours',
      trialDuration: '7 jours',
      trialNote: 'Connectez-vous ou inscrivez-vous pour démarrer votre essai gratuit',
      trialPerks: ['Roadbooks limités à 25 km et 25 waypoints', 'Essai gratuit de 7 jours actif'],
      trialPerksMore: ['TOUTES les fonctionnalités avancées Premium et Pro', 'Téléchargement des roadbooks en PDF et GPX'],
      premiumDuration: 'Par an',
      premiumNote: 'Roadbooks pour usage personnel',
      premiumPerkHead: 'Téléchargement des roadbooks en PDF et GPX',
      premiumPerks: [
        'Export vers tous les lecteurs de roadbook numériques',
        'Export vers les systèmes de suivi de rallye',
        'Impression directe du roadbook en PDF',
        'Formats A5, A4, Legal, Letter et rouleau',
        'Itinéraire qui suit les routes',
        'Import de traces pour créer un roadbook',
        'Fonctions intelligentes de cap compas',
        'Usage non commercial',
      ],
      proDuration: 'Par an',
      proNote: 'Roadbooks pour usage commercial',
      proPlus: 'Tout Premium - PLUS :',
      pro: {
        'pro-license': 'Licence Pro pour épreuves de rallye commerciales',
        'commercial-use': 'Supprime la mention « Not for Commercial Use »',
        'header-image': 'Image d’en-tête de roadbook personnalisée',
        'map-sources': 'Sources cartographiques supplémentaires',
        'custom-icons': 'Import d’icônes de navigation personnalisées',
        'qr-code': 'QR code personnalisé dans l’en-tête et les waypoints',
        'speed-elevation': 'Vitesse et altitude affichées sur la carte',
        'gps-recorder': 'Enregistreur de roadbook GPS EN DIRECT',
        'gps-validator': 'Validateur de roadbook GPS',
        'elevation-profile': 'Profil et données d’altitude',
        'track-color': 'Couleur de trace affichée sur la carte',
        'moto-rolls': 'Rouleaux moto multi-largeurs',
        'waypoint-numbers': 'Numéros de waypoints sur la carte',
        'waypoint-controls': 'Icônes de contrôle des waypoints sur la carte',
        'street-view': 'Image Street View dans le roadbook',
        'track-distance-hover': 'Distance de trace au survol',
        'et-distance': 'Ajout de distance/CAP de trace',
        'tulip-assist': 'Tulip Assist',
        'zone-controls': 'Regroupement, assistance, contrôle de zone',
        'smart-icons': 'Icônes intelligentes par glisser-déposer',
        'kmz-gpx-export': 'Export KMZ et GPX avec détails de l’itinéraire',
      },
    },
  },

  es: {
    title: 'Descargar Rally Navigator – Software de roadbook para Windows, Mac y Linux',
    description:
      'Descarga Rally Navigator para Windows, macOS y Linux. Prueba gratuita de 7 días. Crea roadbooks de rally profesionales conformes a la FIA en tu ordenador.',
    h1: 'Descargar Rally Navigator',
    btnWindows: 'Descargar para Windows 10+ 64 bits',
    btnMac: 'Descargar para macOS',
    btnLinux: 'Descargar para Linux',
    latestVersion: 'Última versión:',
    released: 'Publicada el',
    whatsNew: 'Ver novedades',
    trust: ['Firmado y notarizado por Apple', 'Publicado por Rally Navigator, LLC', 'Descarga HTTPS segura'],
    previousTitle: '¿Problemas con la versión actual? Descarga aquí la versión anterior',
    versionWord: 'Versión',
    howH2: 'Descargar, instalar y crear tu cuenta',
    newH3: '¿Nuevo en Rally Navigator?',
    newSteps: [
      '1) Descarga e instala Rally Navigator',
      `2) Abre el programa, haz clic en ${W('Mi Suscripción')} en el menú superior y luego en ${W('Crear Cuenta')} (o en ${W('Acceso')} si ya tienes una)`,
      '3) Haz clic en el enlace del correo de activación que te enviamos (revisa la carpeta de spam si no llega en unos minutos)',
      '4) Inicia sesión &mdash; empieza tu prueba gratuita de 7 días. ¡Crea tu primer roadbook!',
      '5) Pásate a Premium o Pro para conservar las funciones avanzadas',
    ],
    existingH3: '¿Ya tienes una cuenta?',
    existing: [
      `${W('Ordenador nuevo:')} instala Rally Navigator e inicia sesión con el mismo correo &mdash; tu suscripción se aplica automáticamente en tantos ordenadores tuyos como quieras.`,
      `${W('Ya instalado:')} no hace falta volver a descargarlo. Rally Navigator busca actualizaciones cada vez que se inicia y te ofrece instalar la nueva versión.`,
    ],
    plansH2: 'Planes y precios',
    plansP: `Empieza con la prueba gratuita de 7 días y después elige tu plan dentro de la aplicación, en ${W('Mi Suscripción')}.`,
    trialH2: 'Prueba gratuita de 7 días',
    trialPs: [
      `La ${W('prueba gratuita de 7 días')} de Rally Navigator incluye ${W('todas las funciones Premium y Pro')} &mdash; herramientas de mapa avanzadas, iconos de navegación FIA/Dakar y exportación completa a PDF y GPX. Es la aplicación completa, gratis durante una semana.`,
      `Los roadbooks de los usuarios gratuitos están limitados a ${W('25&nbsp;km o 25 waypoints')}. Crea un roadbook, aprende el flujo de trabajo y descárgalo en PDF y OpenRally GPX.`,
      `¿Listo para pruebas completas? ${R('Pásate a Premium o Pro')} dentro de la aplicación, en ${W('Mi Suscripción')}, para roadbooks ilimitados, sin límite de distancia ni de waypoints.`,
      'Cuando termina la prueba gratuita de 7 días puedes seguir creando roadbooks, pero ya no puedes descargar archivos PDF ni GPX.',
    ],
    newFeaturesH2: 'NOVEDADES',
    newFeatures: [
      'Imágenes de relieve 3D de MapBox',
      'Funciona, guarda e imprime en PDF en tu ordenador',
      'Comparte por Dropbox, iCloud, OneDrive',
      'Waypoints y radios de validación ajustables, visibles en el mapa',
      'Resaltado automático del roadbook en pantalla',
      'Iconos de navegación FIA/Dakar en SVG',
    ],
    feedbackH2: 'DANOS TU OPINIÓN',
    howItWorksH2: 'RALLY NAVIGATOR &#8211; CÓMO FUNCIONA',
    ccFormat: 'Formato cross-country',
    rrFormat: 'Formato rally de carretera',
    featuresA: ['Imágenes de mapa 3D de MapBox y Google', 'Abre, guarda e imprime roadbooks en PDF - sin conexión a la red'],
    featuresB: ['Resaltado mejorado del roadbook, la ruta y el mapa en pantalla', 'Compatibilidad con archivos Google KMZ/KML'],
    featuresC: ['Herramientas de dibujo más rápidas y mejores - barra de herramientas mejorada', 'Compatibilidad con roadbooks digitales + formato OpenRally GPX'],
    ccrrH2: 'ROADBOOKS DE CROSS-COUNTRY Y DE RALLY DE CARRETERA &amp; TRAMOS',
    alt: {
      toolbar: 'Barra de herramientas del editor de roadbooks Rally Navigator',
      ccFormat: 'Formato de roadbook cross-country en Rally Navigator',
      rrFormat: 'Formato de roadbook de rally de carretera en Rally Navigator',
      pdf: 'Rally Navigator imprimiendo un roadbook en PDF con mapas MapBox',
      crossCountry: 'Roadbook de rally cross-country creado con Rally Navigator',
      ccImage: 'Ejemplo de roadbook cross-country',
      rrImage: 'Ejemplo de roadbook de rally de carretera',
    },
    downloadLinkText: 'Descargar Rally Navigator',
    phone: {
      message: 'Rally Navigator es una aplicación de escritorio para Windows, Mac y Linux. Abre esta página en tu ordenador para descargarla.',
      copy: 'Copiar enlace',
      copied: 'Enlace copiado ✓',
    },
    pricing: {
      trialAlt: 'Prueba gratuita',
      trialH3: 'Prueba gratuita de 7 días',
      trialDuration: '7 días',
      trialNote: 'Inicia sesión o regístrate para empezar tu prueba gratuita',
      trialPerks: ['Roadbooks limitados a 25 km y 25 waypoints', 'Prueba gratuita de 7 días activa'],
      trialPerksMore: ['TODAS las funciones avanzadas Premium y Pro', 'Descarga de roadbooks en PDF y GPX'],
      premiumDuration: 'Al año',
      premiumNote: 'Roadbooks para uso personal',
      premiumPerkHead: 'Descarga de roadbooks en PDF y GPX',
      premiumPerks: [
        'Exportación a todos los lectores de roadbook digitales',
        'Exportación a sistemas de seguimiento de rally',
        'Impresión directa del roadbook en PDF',
        'Formatos A5, A4, Legal, Letter y rollo',
        'Ruta que sigue las carreteras',
        'Importa tracks para crear un roadbook',
        'Funciones inteligentes de rumbo de brújula',
        'Uso no comercial',
      ],
      proDuration: 'Al año',
      proNote: 'Roadbooks para uso comercial',
      proPlus: 'Todo lo de Premium - MÁS:',
      pro: {
        'pro-license': 'Licencia Pro para pruebas de rally comerciales',
        'commercial-use': 'Elimina la marca «Not for Commercial Use»',
        'header-image': 'Imagen de cabecera personalizada del roadbook',
        'map-sources': 'Fuentes de mapas adicionales',
        'custom-icons': 'Importación de iconos de navegación personalizados',
        'qr-code': 'Código QR personalizado en la cabecera y los waypoints',
        'speed-elevation': 'Velocidad y altitud en el mapa',
        'gps-recorder': 'Grabador de roadbook GPS EN VIVO',
        'gps-validator': 'Validador de roadbook GPS',
        'elevation-profile': 'Perfil y datos de altitud',
        'track-color': 'Color del track en el mapa',
        'moto-rolls': 'Rollos de moto de varios anchos',
        'waypoint-numbers': 'Números de waypoint en el mapa',
        'waypoint-controls': 'Iconos de control de waypoints en el mapa',
        'street-view': 'Imagen de Street View en el roadbook',
        'track-distance-hover': 'Distancia del track al pasar el ratón',
        'et-distance': 'Añadir distancia/CAP del track',
        'tulip-assist': 'Tulip Assist',
        'zone-controls': 'Reagrupamiento, asistencia, control de zona',
        'smart-icons': 'Iconos inteligentes de arrastrar y soltar',
        'kmz-gpx-export': 'Exportación KMZ y GPX con detalles de la ruta',
      },
    },
  },

  it: {
    title: 'Scarica Rally Navigator – Software per roadbook per Windows, Mac e Linux',
    description:
      'Scarica Rally Navigator per Windows, macOS e Linux. Prova gratuita di 7 giorni. Crea roadbook da rally professionali conformi FIA sul tuo computer.',
    h1: 'Scarica Rally Navigator',
    btnWindows: 'Scarica per Windows 10+ 64 bit',
    btnMac: 'Scarica per macOS',
    btnLinux: 'Scarica per Linux',
    latestVersion: 'Ultima versione:',
    released: 'Pubblicata il',
    whatsNew: 'Scopri le novità',
    trust: ['Firmato e notarizzato da Apple', 'Pubblicato da Rally Navigator, LLC', 'Download HTTPS sicuro'],
    previousTitle: 'Problemi con la versione attuale? Scarica qui la versione precedente',
    versionWord: 'Versione',
    howH2: 'Scaricare, installare e creare l’account',
    newH3: 'Nuovo su Rally Navigator?',
    newSteps: [
      '1) Scarica e installa Rally Navigator',
      `2) Apri il programma, fai clic su ${W('Il mio abbonamento')} nel menu in alto, poi su ${W('Crea Account')} (o su ${W('Accedi')} se ne hai già uno)`,
      '3) Fai clic sul link nell’email di attivazione che ti inviamo (controlla lo spam se non arriva entro qualche minuto)',
      '4) Accedi &mdash; inizia la tua prova gratuita di 7 giorni. Crea il tuo primo roadbook!',
      '5) Passa a Premium o Pro per mantenere le funzioni avanzate',
    ],
    existingH3: 'Hai già un account?',
    existing: [
      `${W('Nuovo computer:')} installa Rally Navigator e accedi con la stessa email &mdash; il tuo abbonamento si applica automaticamente su tutti i computer tuoi che desideri.`,
      `${W('Già installato:')} non serve scaricarlo di nuovo. Rally Navigator cerca gli aggiornamenti a ogni avvio e ti propone di installare la nuova versione.`,
    ],
    plansH2: 'Piani e prezzi',
    plansP: `Inizia con la prova gratuita di 7 giorni, poi scegli il tuo piano nell’app, in ${W('Il mio abbonamento')}.`,
    trialH2: 'Prova gratuita di 7 giorni',
    trialPs: [
      `La ${W('prova gratuita di 7 giorni')} di Rally Navigator include ${W('tutte le funzioni Premium e Pro')} &mdash; strumenti di mappa avanzati, icone di navigazione FIA/Dakar ed esportazione completa in PDF e GPX. È l’app completa, gratis per una settimana.`,
      `I roadbook degli utenti gratuiti sono limitati a ${W('25&nbsp;km o 25 waypoint')}. Crea un roadbook, impara il flusso di lavoro e scaricalo in PDF e OpenRally GPX.`,
      `Pronto per gare complete? ${R('Passa a Premium o Pro')} nell’app, in ${W('Il mio abbonamento')}, per roadbook illimitati, senza limiti di distanza o di waypoint.`,
      'Al termine della prova gratuita di 7 giorni puoi continuare a creare roadbook, ma non puoi più scaricare file PDF o GPX.',
    ],
    newFeaturesH2: 'NOVITÀ',
    newFeatures: [
      'Immagini del terreno 3D MapBox',
      'Funziona, salva e stampa in PDF sul tuo computer',
      'Condivisione tramite Dropbox, iCloud, OneDrive',
      'Waypoint e raggi di validazione regolabili, visibili sulla mappa',
      'Evidenziazione automatica del roadbook sullo schermo',
      'Icone di navigazione FIA/Dakar in SVG',
    ],
    feedbackH2: 'DACCI IL TUO PARERE',
    howItWorksH2: 'RALLY NAVIGATOR &#8211; COME FUNZIONA',
    ccFormat: 'Formato cross-country',
    rrFormat: 'Formato rally su strada',
    featuresA: ['Splendide immagini di mappa 3D MapBox e Google', 'Apri, salva e stampa roadbook in PDF - senza connessione di rete'],
    featuresB: ['Evidenziazione migliorata di roadbook, percorso e mappa sullo schermo', 'Supporto dei file Google KMZ/KML'],
    featuresC: ['Strumenti di disegno più veloci e migliori - barra degli strumenti migliorata', 'Supporto per roadbook digitali + formato OpenRally GPX'],
    ccrrH2: 'ROADBOOK CROSS-COUNTRY E RALLY SU STRADA &amp; PROVE SPECIALI',
    alt: {
      toolbar: 'Barra degli strumenti dell’editor di roadbook Rally Navigator',
      ccFormat: 'Formato di roadbook cross-country in Rally Navigator',
      rrFormat: 'Formato di roadbook da rally su strada in Rally Navigator',
      pdf: 'Rally Navigator che stampa un roadbook PDF con mappe MapBox',
      crossCountry: 'Roadbook da rally cross-country creato con Rally Navigator',
      ccImage: 'Esempio di roadbook cross-country',
      rrImage: 'Esempio di roadbook da rally su strada',
    },
    downloadLinkText: 'Scarica Rally Navigator',
    phone: {
      message: 'Rally Navigator è un’applicazione desktop per Windows, Mac e Linux. Apri questa pagina sul tuo computer per scaricarla.',
      copy: 'Copia link',
      copied: 'Link copiato ✓',
    },
    pricing: {
      trialAlt: 'Prova gratuita',
      trialH3: 'Prova gratuita di 7 giorni',
      trialDuration: '7 giorni',
      trialNote: 'Accedi o registrati per iniziare la prova gratuita',
      trialPerks: ['Roadbook limitati a 25 km e 25 waypoint', 'Prova gratuita di 7 giorni attiva'],
      trialPerksMore: ['TUTTE le funzioni avanzate Premium e Pro', 'Download dei roadbook in PDF e GPX'],
      premiumDuration: 'All’anno',
      premiumNote: 'Roadbook per uso personale',
      premiumPerkHead: 'Download dei roadbook in PDF e GPX',
      premiumPerks: [
        'Esportazione verso tutti i lettori di roadbook digitali',
        'Esportazione verso i sistemi di tracciamento rally',
        'Stampa diretta del roadbook in PDF',
        'Formati A5, A4, Legal, Letter e rotolo',
        'Percorso che segue le strade',
        'Importa tracce per creare un roadbook',
        'Funzioni intelligenti di rotta bussola',
        'Uso non commerciale',
      ],
      proDuration: 'All’anno',
      proNote: 'Roadbook per uso commerciale',
      proPlus: 'Tutto di Premium - IN PIÙ:',
      pro: {
        'pro-license': 'Licenza Pro per gare di rally commerciali',
        'commercial-use': 'Rimuove la dicitura «Not for Commercial Use»',
        'header-image': 'Immagine di intestazione del roadbook personalizzata',
        'map-sources': 'Fonti cartografiche aggiuntive',
        'custom-icons': 'Importazione di icone di navigazione personalizzate',
        'qr-code': 'Codice QR personalizzato nell’intestazione e nei waypoint',
        'speed-elevation': 'Velocità e altitudine sulla mappa',
        'gps-recorder': 'Registratore di roadbook GPS LIVE',
        'gps-validator': 'Validatore di roadbook GPS',
        'elevation-profile': 'Profilo e dati altimetrici',
        'track-color': 'Colore della traccia sulla mappa',
        'moto-rolls': 'Rotoli moto a più larghezze',
        'waypoint-numbers': 'Numeri dei waypoint sulla mappa',
        'waypoint-controls': 'Icone di controllo dei waypoint sulla mappa',
        'street-view': 'Immagine Street View nel roadbook',
        'track-distance-hover': 'Distanza della traccia al passaggio del mouse',
        'et-distance': 'Aggiungi distanza/CAP della traccia',
        'tulip-assist': 'Tulip Assist',
        'zone-controls': 'Riaggruppamento, assistenza, controllo di zona',
        'smart-icons': 'Icone intelligenti trascina e rilascia',
        'kmz-gpx-export': 'Esportazione KMZ e GPX con dettagli del percorso',
      },
    },
  },
  de: {
    title: 'Rally Navigator herunterladen – Roadbook-Software für Windows, Mac und Linux',
    description:
      'Rally Navigator für Windows, macOS und Linux herunterladen. 7 Tage kostenlos testen. Professionelle, FIA-konforme Rallye-Roadbooks am Computer erstellen.',
    h1: 'Rally Navigator herunterladen',
    btnWindows: 'Download für Windows 10+ 64-Bit',
    btnMac: 'Download für macOS',
    btnLinux: 'Download für Linux',
    latestVersion: 'Aktuelle Version:',
    released: 'Veröffentlicht am',
    whatsNew: 'Was ist neu',
    trust: ['Signiert und von Apple notarisiert', 'Herausgeber: Rally Navigator, LLC', 'Sicherer HTTPS-Download'],
    previousTitle: 'Probleme mit der aktuellen Version? Hier die vorherige Version herunterladen',
    versionWord: 'Version',
    howH2: 'Herunterladen, installieren und registrieren',
    newH3: 'Neu bei Rally Navigator?',
    newSteps: [
      '1) Rally Navigator herunterladen und installieren',
      `2) Programm öffnen, im oberen Menü auf ${W('Mein Abo')} klicken, dann auf ${W('Konto erstellen')} (oder ${W('Login')}, wenn Sie schon ein Konto haben)`,
      '3) Klicken Sie auf den Link in der Aktivierungs-E-Mail, die wir Ihnen senden (sehen Sie im Spam-Ordner nach, falls sie nach ein paar Minuten nicht da ist)',
      '4) Anmelden &mdash; Ihre 7-tägige kostenlose Testphase beginnt. Erstellen Sie Ihr erstes Roadbook!',
      '5) Auf Premium oder Pro upgraden, um die erweiterten Funktionen zu behalten',
    ],
    existingH3: 'Sie haben schon ein Konto?',
    existing: [
      `${W('Neuer Computer:')} Rally Navigator installieren und mit derselben E-Mail anmelden &mdash; Ihr Abo gilt automatisch auf beliebig vielen Ihrer eigenen Computer.`,
      `${W('Bereits installiert:')} Sie müssen nichts erneut herunterladen. Rally Navigator sucht bei jedem Start nach Updates und bietet Ihnen die neue Version zur Installation an.`,
    ],
    plansH2: 'Tarife und Preise',
    plansP: `Beginnen Sie mit der 7-tägigen kostenlosen Testphase und wählen Sie dann Ihren Tarif in der App unter ${W('Mein Abo')}.`,
    trialH2: '7 Tage kostenlos testen',
    trialPs: [
      `Die ${W('7-tägige kostenlose Testphase')} von Rally Navigator umfasst ${W('alle Premium- und Pro-Funktionen')} &mdash; erweiterte Kartenwerkzeuge, FIA/Dakar-Navigationssymbole und vollständigen PDF- und GPX-Export. Die komplette App, eine Woche lang kostenlos.`,
      `Roadbooks kostenloser Nutzer sind auf ${W('25&nbsp;km oder 25 Wegpunkte')} begrenzt. Erstellen Sie ein Roadbook, lernen Sie den Ablauf kennen und laden Sie es als PDF und OpenRally GPX herunter.`,
      `Bereit für komplette Veranstaltungen? ${R('Upgraden Sie auf Premium oder Pro')} in der App unter ${W('Mein Abo')} &mdash; für unbegrenzte Roadbooks ohne Distanz- oder Wegpunktlimit.`,
      'Nach Ablauf der 7-tägigen Testphase können Sie weiterhin Roadbooks erstellen, aber keine PDF- oder GPX-Dateien mehr herunterladen.',
    ],
    newFeaturesH2: 'NEUE FUNKTIONEN',
    newFeatures: [
      'MapBox 3D-Geländebilder',
      'Läuft, speichert und druckt PDF auf Ihrem Computer',
      'Teilen über Dropbox, iCloud, OneDrive',
      'Einstellbare Wegpunkt- und Validierungsradien, auf der Karte sichtbar',
      'Automatische Hervorhebung im Roadbook auf dem Bildschirm',
      'FIA/Dakar-Navigationssymbole als SVG',
    ],
    feedbackH2: 'IHRE MEINUNG IST UNS WICHTIG',
    howItWorksH2: 'RALLY NAVIGATOR &#8211; SO FUNKTIONIERT ES',
    ccFormat: 'Cross-Country-Format',
    rrFormat: 'Straßenrallye-Format',
    featuresA: ['Hervorragende MapBox- und Google-3D-Kartenbilder', 'Roadbooks als PDF öffnen, speichern und drucken - ohne Netzwerkverbindung'],
    featuresB: ['Verbesserte Hervorhebung von Roadbook, Route und Karte', 'Unterstützung für Google KMZ/KML-Dateien'],
    featuresC: ['Schnellere und bessere Zeichenwerkzeuge - verbesserte Werkzeugleiste', 'Unterstützung für digitale Roadbooks + OpenRally-GPX-Format'],
    ccrrH2: 'ROADBOOKS FÜR CROSS-COUNTRY, STRASSEN- UND WERTUNGSPRÜFUNGS-RALLYES',
    alt: {
      toolbar: 'Werkzeugleiste des Roadbook-Editors Rally Navigator',
      ccFormat: 'Cross-Country-Roadbook-Format in Rally Navigator',
      rrFormat: 'Straßenrallye-Roadbook-Format in Rally Navigator',
      pdf: 'Rally Navigator druckt ein PDF-Roadbook mit MapBox-Karten',
      crossCountry: 'Mit Rally Navigator erstelltes Cross-Country-Roadbook',
      ccImage: 'Beispiel für ein Cross-Country-Roadbook',
      rrImage: 'Beispiel für ein Straßenrallye-Roadbook',
    },
    downloadLinkText: 'Rally Navigator herunterladen',
    phone: {
      message: 'Rally Navigator ist eine Desktop-App für Windows, Mac und Linux. Öffnen Sie diese Seite auf Ihrem Computer, um sie herunterzuladen.',
      copy: 'Link kopieren',
      copied: 'Link kopiert ✓',
    },
    pricing: {
      trialAlt: 'Kostenlose Testphase',
      trialH3: '7 Tage kostenlos testen',
      trialDuration: '7 Tage',
      trialNote: 'Anmelden oder registrieren, um die Testphase zu starten',
      trialPerks: ['Roadbooks auf 25 km und 25 Wegpunkte begrenzt', 'Aktive 7-tägige Testphase'],
      trialPerksMore: ['ALLE erweiterten Premium- und Pro-Funktionen', 'Roadbook als PDF und GPX herunterladen'],
      premiumDuration: 'Pro Jahr',
      premiumNote: 'Roadbooks für den privaten Gebrauch',
      premiumPerkHead: 'Roadbook als PDF und GPX herunterladen',
      premiumPerks: [
        'Export auf alle digitalen Roadbook-Geräte',
        'Export auf Rallye-Tracking-Geräte',
        'Direkt als PDF oder digitales Roadbook',
        'Unterstützt A5, A4, Legal, Letter und Rolle',
        'Routing-Funktion „Straßen folgen“',
        'Tracks importieren, um ein Roadbook zu erstellen',
        'Intelligente Kompasskurs-Funktionen',
        'Nicht für kommerzielle Nutzung',
      ],
      proDuration: 'Pro Jahr',
      proNote: 'Roadbooks für die kommerzielle Nutzung',
      proPlus: 'Alles aus Premium - PLUS:',
      pro: {
        'pro-license': 'Pro-Lizenz für kommerzielle Rallye-Veranstaltungen',
        'commercial-use': 'Entfernt „Not for Commercial Use“',
        'header-image': 'Eigenes Kopfbild im Roadbook',
        'map-sources': 'Zusätzliche Kartenquellen',
        'custom-icons': 'Eigene Navigationssymbole importieren',
        'qr-code': 'Eigener QR-Code in Kopfzeile und Wegpunkten',
        'speed-elevation': 'Geschwindigkeit und Höhe auf der Karte',
        'gps-recorder': 'GPS-Roadbook-Recorder LIVE',
        'gps-validator': 'GPS-Roadbook-Validator',
        'elevation-profile': 'Höhenprofil und Höhendaten',
        'track-color': 'Trackfarbe auf der Karte',
        'moto-rolls': 'Mehrspaltige Motorrad-Rollen',
        'waypoint-numbers': 'Wegpunktnummern auf der Karte',
        'waypoint-controls': 'Kontrollsymbole der Wegpunkte auf der Karte',
        'street-view': 'Street-View-Bild ins Roadbook',
        'track-distance-hover': 'Trackdistanz beim Darüberfahren',
        'et-distance': 'Trackdistanz/CAP hinzufügen',
        'tulip-assist': 'Tulip Assist',
        'zone-controls': 'Regroup, Service, Zonenkontrolle',
        'smart-icons': 'Smart Icons per Drag-and-drop',
        'kmz-gpx-export': 'KMZ- und GPX-Export mit Routendetails',
      },
    },
  },
  nl: {
    title: 'Rally Navigator downloaden – Roadbook-software voor Windows, Mac en Linux',
    description:
      'Download Rally Navigator voor Windows, macOS en Linux. 7 dagen gratis proberen. Maak professionele, FIA-conforme rallyroadbooks op uw computer.',
    h1: 'Rally Navigator downloaden',
    btnWindows: 'Download voor Windows 10+ 64-bit',
    btnMac: 'Download voor macOS',
    btnLinux: 'Download voor Linux',
    latestVersion: 'Nieuwste versie:',
    released: 'Uitgebracht op',
    whatsNew: 'Bekijk wat er nieuw is',
    trust: ['Ondertekend en door Apple genotariseerd', 'Uitgegeven door Rally Navigator, LLC', 'Veilige HTTPS-download'],
    previousTitle: 'Problemen met de huidige versie? Download hier de vorige versie',
    versionWord: 'Versie',
    howH2: 'Downloaden, installeren en registreren',
    newH3: 'Nieuw bij Rally Navigator?',
    newSteps: [
      '1) Download en installeer Rally Navigator',
      `2) Open het programma (de app is in het Engels), klik in het menu bovenaan op ${W('My Subscription')} en daarna op ${W('Login / Register')} om een account aan te maken`,
      '3) Klik op de link in de activeringsmail die we u sturen (kijk in uw spammap als hij na een paar minuten nog niet binnen is)',
      '4) Log in &mdash; uw gratis proefperiode van 7 dagen begint. Maak uw eerste roadbook!',
      '5) Stap over op Premium of Pro om de geavanceerde functies te houden',
    ],
    existingH3: 'Hebt u al een account?',
    existing: [
      `${W('Nieuwe computer:')} installeer Rally Navigator en log in met hetzelfde e-mailadres &mdash; uw abonnement geldt automatisch op zoveel eigen computers als u wilt.`,
      `${W('Al geïnstalleerd:')} u hoeft niets opnieuw te downloaden. Rally Navigator zoekt bij elke start naar updates en biedt aan de nieuwe versie voor u te installeren.`,
    ],
    plansH2: 'Abonnementen en prijzen',
    plansP: `Begin met de gratis proefperiode van 7 dagen en kies daarna uw abonnement in de app onder ${W('My Subscription')}.`,
    trialH2: '7 dagen gratis proberen',
    trialPs: [
      `De ${W('gratis proefperiode van 7 dagen')} van Rally Navigator bevat ${W('alle Premium- en Pro-functies')} &mdash; geavanceerde kaarttools, FIA/Dakar-navigatiesymbolen en volledige PDF- en GPX-export. De complete app, een week lang gratis.`,
      `Roadbooks van gratis gebruikers zijn beperkt tot ${W('25&nbsp;km of 25 waypoints')}. Maak een roadbook, leer de werkwijze en download het als PDF en OpenRally GPX.`,
      `Klaar voor volledige evenementen? ${R('Stap over op Premium of Pro')} in de app onder ${W('My Subscription')} &mdash; voor onbeperkte roadbooks zonder limiet op afstand of waypoints.`,
      'Na afloop van de proefperiode van 7 dagen kunt u nog steeds roadbooks maken, maar geen PDF- of GPX-bestanden meer downloaden.',
    ],
    newFeaturesH2: 'NIEUWE FUNCTIES',
    newFeatures: [
      'MapBox 3D-terreinbeelden',
      'Draait, bewaart en print PDF op uw eigen computer',
      'Delen via Dropbox, iCloud, OneDrive',
      'Instelbare waypoint- en validatiestraal, zichtbaar op de kaart',
      'Automatische markering in het roadbook op het scherm',
      'FIA/Dakar-navigatiesymbolen in SVG',
    ],
    feedbackH2: 'DEEL UW MENING MET ONS',
    howItWorksH2: 'RALLY NAVIGATOR &#8211; ZO WERKT HET',
    ccFormat: 'Cross-countryformaat',
    rrFormat: 'Wegrallyformaat',
    featuresA: ['Prachtige MapBox- en Google 3D-kaartbeelden', 'Roadbooks als PDF openen, bewaren en printen - zonder netwerkverbinding'],
    featuresB: ['Verbeterde markering van roadbook, route en kaart op het scherm', 'Ondersteuning voor Google KMZ/KML-bestanden'],
    featuresC: ['Snellere en betere tekentools - verbeterde werkbalk', 'Ondersteuning voor digitale roadbooks + OpenRally GPX-formaat'],
    ccrrH2: 'ROADBOOKS VOOR CROSS-COUNTRY, WEG- EN KLASSEMENTSPROEFRALLY’S',
    alt: {
      toolbar: 'Werkbalk van de roadbook-editor Rally Navigator',
      ccFormat: 'Cross-country-roadbookformaat in Rally Navigator',
      rrFormat: 'Wegrally-roadbookformaat in Rally Navigator',
      pdf: 'Rally Navigator print een PDF-roadbook met MapBox-kaarten',
      crossCountry: 'Cross-country-roadbook gemaakt met Rally Navigator',
      ccImage: 'Voorbeeld van een cross-country-roadbook',
      rrImage: 'Voorbeeld van een wegrally-roadbook',
    },
    downloadLinkText: 'Rally Navigator downloaden',
    phone: {
      message: 'Rally Navigator is een desktop-app voor Windows, Mac en Linux. Open deze pagina op uw computer om te downloaden.',
      copy: 'Link kopiëren',
      copied: 'Link gekopieerd ✓',
    },
    pricing: {
      trialAlt: 'Gratis proefperiode',
      trialH3: '7 dagen gratis proberen',
      trialDuration: '7 dagen',
      trialNote: 'Log in of registreer om uw proefperiode te starten',
      trialPerks: ['Roadbooks beperkt tot 25 km en 25 waypoints', 'Actieve proefperiode van 7 dagen'],
      trialPerksMore: ['ALLE geavanceerde Premium- en Pro-functies', 'Roadbook downloaden als PDF en GPX'],
      premiumDuration: 'Per jaar',
      premiumNote: 'Roadbooks voor persoonlijk gebruik',
      premiumPerkHead: 'Roadbook downloaden als PDF en GPX',
      premiumPerks: [
        'Export naar alle digitale roadbookapparaten',
        'Export naar rally-trackingapparaten',
        'Direct naar PDF of digitale roadbooks',
        'Ondersteunt A5, A4, Legal, Letter en rol',
        'Routefunctie „Wegen volgen”',
        'Tracks importeren om een roadbook te maken',
        'Slimme kompaskoersfuncties',
        'Niet voor commercieel gebruik',
      ],
      proDuration: 'Per jaar',
      proNote: 'Roadbooks voor commercieel gebruik',
      proPlus: 'Alles uit Premium - PLUS:',
      pro: {
        'pro-license': 'Pro-licentie voor commerciële rally-evenementen',
        'commercial-use': 'Verwijdert „Not for Commercial Use”',
        'header-image': 'Eigen kopafbeelding in het roadbook',
        'map-sources': 'Extra kaartbronnen',
        'custom-icons': 'Eigen navigatiesymbolen importeren',
        'qr-code': 'Eigen QR-code in koptekst en waypoints',
        'speed-elevation': 'Snelheid en hoogte op de kaart',
        'gps-recorder': 'GPS-roadbookrecorder LIVE',
        'gps-validator': 'GPS-roadbookvalidator',
        'elevation-profile': 'Hoogteprofiel en hoogtedata',
        'track-color': 'Trackkleur op de kaart',
        'moto-rolls': 'Motorrollen in meerdere breedtes',
        'waypoint-numbers': 'Waypointnummers op de kaart',
        'waypoint-controls': 'Controlesymbolen van waypoints op de kaart',
        'street-view': 'Street View-afbeelding in het roadbook',
        'track-distance-hover': 'Trackafstand bij aanwijzen met de muis',
        'et-distance': 'Trackafstand/CAP toevoegen',
        'tulip-assist': 'Tulip Assist',
        'zone-controls': 'Regroup, service, zonecontrole',
        'smart-icons': 'Slimme symbolen slepen en neerzetten',
        'kmz-gpx-export': 'KMZ- en GPX-export met routedetails',
      },
    },
  },
};
